import * as api from '../api';
import OVERLAY_STYLES from './styles';
import { Storage, STORAGE_KEYS } from './store';

/**
 * Permet de charger une vue HTML
 * @param {*} filepath Vue à charger
 * @param {*} postLoadCallback Fonction exécutée après chargement de la vue
 */
export function loadFragment(filepath, postLoadCallback) {
    fetch(filepath)
        .then(response => response.text())
        .then(html => {
            document.getElementById('container').innerHTML = html;
            postLoadCallback();
        })
        .catch(err => console.error(err));
}

/**
 * Fonction de conversion de paramètre de carte provenant de l'api pour leaflet
 * @param {*} settings 
 * @returns 
 */
export function convertMapSettingsToLeaflet(settings) {
    return Object.fromEntries(settings.map(setting => [setting.name, setting.value]));
}

/**
 * Construit le leaflet layer à partir d'un service layer
 * reçu par l'api
 * @param {*} srv 
 * @returns 
 */
function factory(srv) {
    if ('isTileService' in srv && !srv.isTileService) {
        return L.lazyGeoJSON(srv.baseUrl, {
            ...convertMapSettingsToLeaflet(srv.settings),
            onEachFeature: (feature, layer) => {
                layer.on('click', (e) => {
                    layer.bindPopup(L.overlayPopup(feature.properties, { latlng: e.latlng, featureId: feature.id })).openPopup();
                });
                layer.bindTooltip(feature.properties.name ?? '--');

                const style = OVERLAY_STYLES[srv.name ?? 'default'];

                if (typeof style === 'function') {
                    layer.setStyle({
                        ...style(feature.properties),
                        ...(feature.properties.style || {})
                    });
                }
            }
        });
    } else {
        return L.tileLayer(srv.baseUrl, convertMapSettingsToLeaflet(srv.settings));
    }
}

/**
 * Convertit les layers reçu par l'api en leaflet layer
 * @param {*} layer 
 * @returns 
 */
export function convertToLayer(layer) {
    return 'type' in layer && layer.type === 'FeatureCollection'
        ? L.geoJSON(layer, {
            pointToLayer: (geoJSON, latlng) => {
                const type = geoJSON.properties?.type?.name ?? geoJSON.properties?.type ?? 'default';
                return L.gendlocMarker(type, latlng, { properties: geoJSON.properties, featureId: geoJSON.id });
            },
            style: (feature) => {
                const type = feature.properties?.type?.name ?? feature.properties?.type;
                const style = OVERLAY_STYLES[type];
                const defaultStyle = typeof style === 'function' ? style(feature.properties) : {};
                const overrideStyle = typeof feature.properties.style === 'string' 
                    ? JSON.parse(feature.properties.style) 
                    : (feature.properties.style || {});

                return {
                    ...defaultStyle,
                    ...overrideStyle
                };
            }
        })
        : factory(layer); 
}

/**
 * Map des extensions de fichier => type de fichier
 */
export const FILE_EXT_TYPE = {
    'gpx': 'trace',
    'geojson': 'trace',
    'gml': 'trace',
    'kml': 'trace',
    'png': 'photo',
    'jpg': 'photo',
    'viewshed': 'viewshed',
};

/**
 * Map de type d'overlay avec le type de figure Geoman
 */
export const TYPE_OVERLAY_GEOMETRY = {
    'point': 'Marker',
    'polygone': 'Polygon',
    'polyligne': 'Line'
};

export function getActivityName(activity) {
    if (!activity) return '';
    const activityId = (typeof activity === 'object') ? (activity.id || '') : activity;
    if (typeof activity === 'object' && activity.name) {
        return activity.name;
    }
    const storedActivities = Storage.get(STORAGE_KEYS.ACTIVITY) || [];
    const activities = storedActivities.flatMap(cat => cat.activities || (cat.id ? [cat] : []));
    const found = activities.find(a => String(a.id) === String(activityId));
    return found ? found.name : activityId;
}

export function getEventName(event) {
    const activityName = getActivityName(event.activity);
    const activity = activityName ? ` ${activityName}` : '';

    const hour = (new Date(event.created_at)).toLocaleTimeString()
        .split(":")
        .slice(0,2)
        .join(":");

    const displayId = event.local_id;

    return `${displayId} - ${hour} - ${activity}`;
}

export function uploadFile(map, fileAllowed) {
    return () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = fileAllowed;
        input.style.display = 'none'; 

        input.addEventListener('change', () => {
            const file = input.files[0];
            const ext = file.name.split('.').pop().toLowerCase();
            const displayName = prompt("Comment voulez vous appeler ce fichier ?") ?? file.name;

            api.attachFiletoEvent(
                map.eventId, file, FILE_EXT_TYPE[ext] ?? 'document', displayName
            ).then(attached => {
                if (map._eventLayer.fetchDrawableAttachment) {
                    map._eventLayer.fetchDrawableAttachment(attached.id)
                }

                M.toast({html: 'Document sauvegardé', classes: 'green accent-4'});
            });
        });

        document.body.appendChild(input);
        input.click();
        input.remove();
    };
}

export function getDiffDateInSec(date1, date2) {
    return (date1.getTime() - date2.getTime()) / 1000;
}

export async function writeClipboardText(text) {
    try {
       await navigator.clipboard.writeText(text);
       M.toast({html: 'Texte copié', classes: 'green accent-4'});
    } catch (error) {
        console.error(error.message);
       M.toast({html: 'Erreur lors de la copie', classes: 'red accent-4'});
    }
}

/**
 * Compare deux objets de manière profonde pour déterminer s'ils sont identiques
 * @param {*} obj1
 * @param {*} obj2
 * @returns
 */
export function isDeepEqual(obj1, obj2) {
    if (obj1 === obj2) return true;

    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
        return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        if (!keys2.includes(key)) {
            return false;
        }
        if (!isDeepEqual(obj1[key], obj2[key])) {
            return false;
        }
    }

    return true;
}

/**
 * Ouvre une nouvelle fenêtre ou focus une fenêtre existante sans la recharger
 * @param {string} url
 * @param {string} name
 */
export function openOrFocus(url, name) {
    const w = window.open('', name);
    if (w.location.href === 'about:blank' || w.location.href === '') {
        w.location.href = url;
    }
    w.focus();
}